import { create } from "zustand"

import type { ChatSlice } from "./slices/chat-slice"
import type { Document } from "@/api/models"

import { getAvailableIndexesForSession } from "@/features/navigator/utils/available-indexes"
import { SettingsService } from "@/services/settings-service"

import { createChatSlice } from "./slices/chat-slice"
import { createNamespacedStore } from "./store-factory"

export type Libraries = Record<string, { checked: boolean; name: string; description: string }>

type LibraryStore = {
  enabledLibraries: Libraries
  toggleLibraryChecked: (libraryId: string) => void
  toggleAllLibraries: (enable: boolean) => void
  initLibraries: () => void
  setEnabledLibraries: (libraries: Libraries) => void
}

export type NavigatorStore = ChatSlice &
  LibraryStore & {
    citations: string[]
    /** Merged library documents from assistant message context (for display names: original_sourcefile). */
    citationLibraryDocuments: [string, Document][]
    addCitations: (citations: string[]) => void
    mergeCitationLibraryDocuments: (documents: [string, Document][] | undefined) => void
    setCitations: (citations: string[]) => void
    activeCitations: string[]
    toggleActiveCitations: (citations: string[]) => void
    toggleActiveCitationsInLibrary: (citations: string[], checked: boolean) => void
    setActiveCitations: (citations: string[]) => void
  }

const defaultEnabledLibraries: NavigatorStore["enabledLibraries"] = {}

/**
 * Factory function to create a new Navigator store instance.
 * Used internally by createNamespacedStore to create per-sessionType instances.
 */
function createNavigatorStoreInstance(sessionType: string) {
  return create<NavigatorStore>()((set, get, api) => ({
    ...createChatSlice(set, get, api),
    citations: [],
    citationLibraryDocuments: [],
    addCitations(citations: string[]) {
      set((state) => {
        const newCitations = citations.filter((citation) => !state.citations.includes(citation))
        return { citations: [...state.citations, ...newCitations] }
      })
    },
    mergeCitationLibraryDocuments(documents) {
      if (!documents?.length) {
        return
      }
      set((state) => {
        const map = new Map<string, [string, Document]>()
        for (const tuple of state.citationLibraryDocuments) {
          const [libraryId, doc] = tuple
          const key = `${libraryId}:${doc.sourcefile ?? ""}`
          map.set(key, tuple)
        }
        for (const tuple of documents) {
          const [libraryId, doc] = tuple
          const key = `${libraryId}:${doc.sourcefile ?? ""}`
          map.set(key, tuple)
        }
        return { citationLibraryDocuments: [...map.values()] }
      })
    },
    setCitations(citations: string[]) {
      if (citations.length === 0) {
        set({ citations: [], citationLibraryDocuments: [] })
      } else {
        set({ citations })
      }
    },
    activeCitations: [],
    toggleActiveCitations(citations: string[]) {
      const updatedActiveCitations = [...get().activeCitations]

      for (const citation of citations) {
        const citationIndex = updatedActiveCitations.indexOf(citation)

        if (citationIndex !== -1) {
          updatedActiveCitations.splice(citationIndex, 1)
        } else {
          updatedActiveCitations.push(citation)
        }
      }

      set({ activeCitations: updatedActiveCitations })
    },
    toggleActiveCitationsInLibrary(citations: string[], checked: boolean) {
      let updatedActiveCitations = [...get().activeCitations]

      if (checked) {
        const citationsToAdd = citations.filter(
          (citation) => !updatedActiveCitations.includes(citation)
        )
        updatedActiveCitations = [...updatedActiveCitations, ...citationsToAdd]
      } else {
        updatedActiveCitations = updatedActiveCitations.filter(
          (citation) => !citations.includes(citation)
        )
      }

      set({ activeCitations: updatedActiveCitations })
    },
    setActiveCitations(citations: string[]) {
      set({ activeCitations: citations })
    },
    enabledLibraries: defaultEnabledLibraries,
    setEnabledLibraries(libraries: Libraries) {
      set({ enabledLibraries: libraries })
    },
    toggleLibraryChecked(libraryId) {
      const enabledLibraries = { ...get().enabledLibraries }

      // If library doesn't exist, initialize it from SettingsService
      if (!(libraryId in enabledLibraries)) {
        const settingsService = SettingsService.getInstance()
        const libConfig = settingsService.settings.indexes.find((idx) => idx.id === libraryId)

        // Check if library is available for this sessionType (not blocked)
        if (libConfig) {
          const isBlocked =
            libConfig.blocked_modules &&
            libConfig.blocked_modules.length > 0 &&
            libConfig.blocked_modules.includes(sessionType)

          if (!isBlocked) {
            enabledLibraries[libraryId] = {
              checked: true, // Set to true since we're toggling it on
              name: libConfig.display_name,
              description: libConfig.description,
            }
            set({ enabledLibraries })
          }
        }
        return
      }

      // Library exists, just toggle it
      const newChecked = !enabledLibraries[libraryId].checked
      enabledLibraries[libraryId].checked = newChecked
      set({ enabledLibraries })
    },
    toggleAllLibraries(enable) {
      const enabledLibraries = { ...get().enabledLibraries }
      for (const libraryId in enabledLibraries) {
        enabledLibraries[libraryId].checked = enable
      }
      set({ enabledLibraries })
    },
    initLibraries() {
      const enabledLibraries = getAvailableIndexesForSession(sessionType).reduce((acc, index) => {
        return {
          ...acc,
          [index.id]: {
            checked: index.default_enabled,
            name: index.display_name,
            description: index.description,
          },
        }
      }, {})

      set({ enabledLibraries })
    },
  }))
}

/**
 * Hook to get Navigator store isolated by sessionType.
 * Each sessionType gets its own store instance for isolated library and citation state.
 *
 * @param sessionType - Session type identifier (e.g., "variant-a", "variant-b")
 * @returns Zustand store hook for the specific sessionType
 */
export function useNavigatorStore(sessionType: string) {
  return createNamespacedStore("navigator", sessionType, () =>
    createNavigatorStoreInstance(sessionType)
  )
}
